import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  locationDayReportsGetBusinessReport,
  locationEndofdaySettingsUpdateBusinessReportSettings,
  printerGetAllByLocationId,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import { DateInput } from "../ui/date-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { BusinessReportPreview } from "./business-report-preview";
import type { BusinessReportSettings } from "./business-report-types";
import {
  checkReportHasRealData,
  formatReportForDisplay,
  previewData,
} from "./business-report-types";

export function EndOfDayBusinessSettingsForm({
  settings,
  locationId,
  location,
}: {
  settings: BusinessReportSettings | null;
  locationId: string;
  location: {
    id: string;
    name: string;
    address: {
      id: string;
      addressLine1: string;
      addressLine2: string | null;
      city: string;
      zipCode: number;
      state: string;
    } | null;
  };
}) {
  const dateForm = useForm({
    defaultValues: {
      reportDate: new Date(),
    },
  });

  const currentDate = dateForm.watch("reportDate");

  const { data: printers } = useQuery(
    convexQuery(printerGetAllByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const {
    data: report,
    isPending: isLoadingReport,
    error: reportError,
  } = useQuery(
    convexQuery(locationDayReportsGetBusinessReport, {
      locationId: toConvexTableId("locations", locationId),
      date: currentDate.getTime(),
    })
  );

  const updateSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: BusinessReportSettings;
    }) =>
      convexClient.mutation(
        locationEndofdaySettingsUpdateBusinessReportSettings,
        data
      ),
    onSuccess: () => {
      ProductEvents.settingsUpdated("business-report");
    },
  });

  const hasRealData = checkReportHasRealData(report);
  const isPreviewData = !(isLoadingReport || reportError || hasRealData);
  const displayData =
    hasRealData && report ? formatReportForDisplay(report) : previewData;

  const form = useForm<BusinessReportSettings>({
    defaultValues: settings ?? {
      showOrdersAndBillsSummary: true,
      showSalesCategoriesSummary: true,
      showSalesAndTaxesSummary: true,
      showDevicesSummary: true,
      showVoidedItems: true,
      showDiscountedItems: true,
      showRefundedItems: true,
      showManagerCodes: true,
      reprintBusinessReportAtEndOfDay: true,
      showDividers: true,
      printerDeviceId: "all",
      printAutomaticallyAtEndOfDay: true,
      printCopies: 1,
    },
  });

  const [formValues, setFormValues] = useState<BusinessReportSettings>(
    form.getValues()
  );

  useEffect(() => {
    if (settings) {
      form.reset(settings);
      setFormValues(settings);
    }
  }, [settings, form]);

  const update = useDebouncedCallback(() => {
    const values = form.getValues();
    updateSettingsMutation.mutate({
      locationId: toConvexTableId("locations", locationId),
      value: values,
    });
  }, 1000);

  const {
    showOrdersAndBillsSummary,
    showSalesCategoriesSummary,
    showSalesAndTaxesSummary,
    showDevicesSummary,
    showVoidedItems,
    showDiscountedItems,
    showRefundedItems,
    showManagerCodes,
    showDividers,
  } = formValues;

  const handleToggleChange = (
    name: keyof BusinessReportSettings,
    checked: boolean
  ) => {
    form.setValue(name, checked, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setFormValues((prev) => ({
      ...prev,
      [name]: checked,
    }));
    update();
  };

  return (
    <div className="flex items-start gap-6">
      <BusinessReportPreview
        address={location.address}
        currentDate={currentDate}
        displayData={displayData}
        hasRealData={hasRealData}
        isLoadingReport={isLoadingReport}
        isPreviewData={isPreviewData}
        locationName={location.name}
        reportError={reportError}
        showDividers={showDividers}
        showManagerCodes={showManagerCodes}
        visibilitySettings={{
          showOrdersAndBillsSummary,
          showSalesCategoriesSummary,
          showSalesAndTaxesSummary,
          showDevicesSummary,
          showVoidedItems,
          showDiscountedItems,
          showRefundedItems,
        }}
      />

      <div className="w-full flex-1" id="receipt-info">
        <div className="mb-6">
          <h3 className="text-foreground text-weight-medium mb-2 text-balance">
            Select Report Date
          </h3>
          <FormProvider {...dateForm}>
            <DateInput name="reportDate" placeholder="Select a date" />
          </FormProvider>
        </div>

        <Form {...form}>
          <form className="space-y-4">
            <div>
              <h3 className="text-foreground text-weight-medium text-balance">
                Report Information
              </h3>
              <p className="body-sm text-muted-foreground">
                Configure which sections to show in the business report.
              </p>
              <hr className="bg-border mt-4 h-px border-none" />
            </div>

            <FormField
              control={form.control}
              name="showOrdersAndBillsSummary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showOrdersAndBillsSummary", v)
                        }
                      />
                    </FormControl>
                    Show orders and bills summary
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showSalesCategoriesSummary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showSalesCategoriesSummary", v)
                        }
                      />
                    </FormControl>
                    Show sales categories summary
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showSalesAndTaxesSummary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showSalesAndTaxesSummary", v)
                        }
                      />
                    </FormControl>
                    Show sales and taxes summary
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showDevicesSummary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showDevicesSummary", v)
                        }
                      />
                    </FormControl>
                    Show devices summary
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showVoidedItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showVoidedItems", v)
                        }
                      />
                    </FormControl>
                    Show voided items
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showDiscountedItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showDiscountedItems", v)
                        }
                      />
                    </FormControl>
                    Show discounted items
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showRefundedItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showRefundedItems", v)
                        }
                      />
                    </FormControl>
                    Show refunded items
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showManagerCodes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showManagerCodes", v)
                        }
                      />
                    </FormControl>
                    Show permission codes
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <h3 className="text-foreground text-weight-medium py-5 text-balance">
                Print Settings
              </h3>
              <hr className="bg-border mb-4 h-px border-none" />
            </div>

            <FormField
              control={form.control}
              name="printerDeviceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground">
                    Select printer device
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        update();
                      }}
                      value={field.value ?? "all"}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Any Available Printer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Printers</SelectItem>
                        {printers?.map((printer) => (
                          <SelectItem key={printer._id} value={printer._id}>
                            {printer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p className="body-sm text-muted-foreground">
                    Choose which printer device to use for printing the business
                    report. Select "Any Available Printer" to allow printing on
                    any configured printer.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="printAutomaticallyAtEndOfDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("printAutomaticallyAtEndOfDay", v)
                        }
                      />
                    </FormControl>
                    Print automatically at End of Day
                  </FormLabel>
                  <p className="body-sm text-muted-foreground ml-12">
                    When the day is ended, the business report will be
                    automatically printed.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showDividers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground flex items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) =>
                          handleToggleChange("showDividers", v)
                        }
                      />
                    </FormControl>
                    Show dividers
                  </FormLabel>
                  <p className="body-sm text-muted-foreground ml-12">
                    Sections will be visually separated by dashed lines.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
}
